/**
 * SourceTrendChart (Spec 044)
 *
 * Multi-line time-series chart showing retrieval rate trends for top domains.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { EmptyChart } from './ui/EmptyChart.js';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  inkAtStep,
  AXIS_STROKE,
  GRID_STROKE,
  GRID_DASH,
  AXIS_TICK,
  CHART_MARGIN,
  TOOLTIP_CONTAINER,
  TOOLTIP_LABEL,
  TOOLTIP_ROW,
  LEGEND_LABEL,
  LEGEND_DOT,
} from './chartTheme.js';
import { TableSkeleton } from './Skeleton.js';

// Spec 234: lines are ink over a grey trend, not a categorical ramp — up to
// ten domains can be plotted at once here, so identity comes from the direct
// label in the legend/tooltip, and each line's RANK (not hue) comes from an
// opacity step of the same ink colour. `stroke` resolves to the identical
// ink value for every line (234.A.1 reads one hue); only strokeOpacity
// varies, which is how the top domain reads as the most prominent line
// without a second colour ever entering the chart.
const domainColor = inkAtStep;

/** House tooltip matching VisibilityChart's styling (visual only). */
function TrendTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className={TOOLTIP_CONTAINER}>
      <p className={TOOLTIP_LABEL}>{label}</p>
      {payload
        .filter((p) => p.value != null)
        .map((p) => (
          <div key={p.dataKey} className={TOOLTIP_ROW}>
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="font-medium text-slate-700">{p.dataKey}:</span>
            <span className="text-slate-700">{p.value}%</span>
          </div>
        ))}
    </div>
  );
}

interface TrendDomain {
  domain_id: string;
  domain: string;
  domain_type: string;
  series: Array<{ period: string; retrieval_rate: number; sample_size: number }>;
}

interface TrendResponse {
  data: TrendDomain[];
}

interface SourceTrendChartProps {
  engine?: string;
  dateRange?: string;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  // The API sends a full ISO timestamp (e.g. 2026-04-20T00:00:00.000Z), but a
  // bare YYYY-MM-DD is also possible. Strip any time portion FIRST so the
  // date-part split below gets exactly 3 clean numeric fields — otherwise
  // `"2026-04-20T00:00:00.000Z".split('-')` yields ["2026","04","20T00:00:00.000Z"]
  // and Number(parts[2]) is NaN, leaving the raw ISO on the axis (spec 119).
  const datePart = dateStr.split('T')[0]!;
  // Parse YYYY-MM-DD directly to avoid timezone/Invalid Date issues
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }
  // Fallback: standard parse of the original string (handles full ISO directly).
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SourceTrendChart({ engine, dateRange }: SourceTrendChartProps) {
  const { selectedBrand, user } = useBrand();
  const [data, setData] = useState<TrendDomain[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchTrends = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (engine) params.set('engine', engine);
      if (dateRange) {
        const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
        const from = new Date();
        from.setDate(from.getDate() - days);
        params.set('date_from', from.toISOString().split('T')[0]!);
      }
      params.set('limit', '10');
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/trends?${params.toString()}`;
      const result = await apiGet<TrendResponse>(url);
      setData(result.data);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, engine, dateRange]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  if (loading) {
    return (
      <div className="h-64">
        <TableSkeleton rows={4} cols={5} />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      // Spec 248 (review P1-7): Kridt's Empty chart at the chart's real height.
      <EmptyChart
        height={256}
        title="No source trend yet"
        text="It draws once responses have been captured and analyzed."
        action={<Link to="/prompts" className="hub-btn hub-btn--ghost">Set up prompts</Link>}
        testId="source-trend-empty"
      />
    );
  }

  // Build chart data: one point per date with columns for each domain
  const allDates = new Set<string>();
  for (const domain of data) {
    for (const point of domain.series) {
      allDates.add(point.period);
    }
  }

  const sortedDates = Array.from(allDates).sort();
  const chartData = sortedDates.map((date) => {
    const point: Record<string, string | number | null> = { date: formatDate(date) };
    for (const domain of data) {
      const match = domain.series.find((s) => s.period === date);
      point[domain.domain] = match ? Math.round(match.retrieval_rate * 1000) / 10 : null;
    }
    return point;
  });

  return (
    <div data-testid="source-trend-chart">
      {/* House legend matching VisibilityChart (visual only). */}
      <div className="mb-3 flex flex-wrap items-center gap-4">
        {data.map((domain, idx) => (
          <div key={domain.domain_id} className="flex items-center gap-1.5">
            <span
              className={LEGEND_DOT}
              style={{ backgroundColor: domainColor(idx) }}
            />
            <span className={LEGEND_LABEL}>{domain.domain}</span>
          </div>
        ))}
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} />
            <XAxis dataKey="date" tick={AXIS_TICK} stroke={AXIS_STROKE} tickFormatter={(d: string) => formatDate(d)} />
            <YAxis domain={[0, 100]} tick={AXIS_TICK} stroke={AXIS_STROKE} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip content={<TrendTooltip />} />
            {data.map((domain, idx) => (
              <Line
                key={domain.domain_id}
                type="monotone"
                dataKey={domain.domain}
                stroke={domainColor(idx)}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
